// ─────────────────────────────────────────────────────────────
// MODAL CONTRAT DE LOCATION — formulaire → signatures → PDF
// Le PDF est téléchargé ET archivé dans Firebase Storage
// (lien enregistré sur la chambre : room.contractUrl)
// ─────────────────────────────────────────────────────────────
import { useEffect, useState } from "react";
import { Modal, Btn, Input } from "./UI";
import SignaturePad from "./SignaturePad";
import { genererContrat } from "../utils/contrat";
import { saveRoomContract } from "../firebase/services";

const EMPTY_LOC = { nom: "", naissance: "", telephone: "", etablissement: "" };

export default function ContratModal({ open, onClose, room }) {
  const [form, setForm] = useState({
    dateEntree: new Date().toISOString().slice(0, 10),
    duree: "12 mois",
    loyer: "27500",
    caution: "27500",
    preavisJours: "30",
    bailleurRepresentant: "",
    faitA: "Koulikoro",
  });
  const [loc1, setLoc1] = useState(EMPTY_LOC);
  const [loc2, setLoc2] = useState(EMPTY_LOC);
  const [deuxLocataires, setDeuxLocataires] = useState(false);
  const [sigBailleur, setSigBailleur] = useState(null);
  const [sigLoc1, setSigLoc1] = useState(null);
  const [sigLoc2, setSigLoc2] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  // Pré-remplissage depuis la chambre à l'ouverture
  useEffect(() => {
    if (open && room) {
      setForm(f => ({
        ...f,
        loyer: String(room.loyer || 27500),
        caution: String(room.loyer || 27500),
      }));
      setLoc1({ ...EMPTY_LOC, nom: room.tenantName || "", telephone: room.tenantPhone || "" });
      setLoc2(EMPTY_LOC);
      setDeuxLocataires(false);
      setSigBailleur(null); setSigLoc1(null); setSigLoc2(null);
      setDone(false); setError("");
    }
  }, [open, room]);

  const canGenerate = loc1.nom.trim() && form.dateEntree && sigBailleur && sigLoc1
    && (!deuxLocataires || (loc2.nom.trim() && sigLoc2));

  const handleGenerate = async () => {
    if (!canGenerate || !room) return;
    setGenerating(true);
    setError("");
    try {
      const doc = genererContrat({
        chambre: room.number,
        dateEntree: form.dateEntree,
        duree: form.duree,
        loyer: form.loyer,
        caution: form.caution,
        preavisJours: form.preavisJours,
        bailleurRepresentant: form.bailleurRepresentant.trim(),
        faitA: form.faitA.trim() || "Koulikoro",
        faitLe: new Date().toISOString(),
        locataire1: loc1,
        locataire2: deuxLocataires ? loc2 : null,
        signatureBailleur: sigBailleur,
        signatureLocataire1: sigLoc1,
        signatureLocataire2: deuxLocataires ? sigLoc2 : null,
      });

      // 1. Téléchargement local immédiat
      const fileName = `Contrat_Ch${room.number}_${loc1.nom.trim().replace(/\s+/g, "_")}.pdf`;
      doc.save(fileName);

      // 2. Archivage dans Firebase Storage (lien sur la chambre)
      try {
        const blob = doc.output("blob");
        await saveRoomContract(room.id, blob, { tenantName: loc1.nom.trim() });
      } catch (e) {
        // L'archivage peut échouer hors-ligne — le PDF local est déjà téléchargé
        console.warn("Archivage Storage différé :", e);
      }

      setDone(true);
    } catch (e) {
      console.error(e);
      setError("Erreur lors de la génération. Réessayez.");
    }
    setGenerating(false);
  };

  return (
    <Modal open={open} onClose={onClose} title={`Contrat — Chambre ${room?.number || ""}`}>
      {room && !done && (
        <div>
          {room.contractUrl && (
            <a href={room.contractUrl} target="_blank" rel="noreferrer"
               style={{ display: "block", background: "#e3f1fb", borderRadius: 10, padding: "10px 12px", marginBottom: 14, fontSize: 12.5, color: "#1a3a5c", textDecoration: "none", fontWeight: 600 }}>
              📎 Un contrat existe déjà pour cette chambre ({room.contractTenantName || "locataire"}) — cliquez pour l'ouvrir. En générer un nouveau le remplacera.
            </a>
          )}

          {/* ── Conditions ── */}
          <p style={S.section}>Conditions du bail</p>
          <div style={{ display: "flex", gap: 10 }}>
            <div style={{ flex: 1 }}><Input label="Date d'entrée *" type="date" value={form.dateEntree} onChange={e => setForm(f => ({ ...f, dateEntree: e.target.value }))} /></div>
            <div style={{ flex: 1 }}><Input label="Durée" value={form.duree} onChange={e => setForm(f => ({ ...f, duree: e.target.value }))} placeholder="12 mois" /></div>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <div style={{ flex: 1 }}><Input label="Loyer mensuel (FCFA)" type="number" value={form.loyer} onChange={e => setForm(f => ({ ...f, loyer: e.target.value }))} /></div>
            <div style={{ flex: 1 }}><Input label="Caution (FCFA)" type="number" value={form.caution} onChange={e => setForm(f => ({ ...f, caution: e.target.value }))} /></div>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <div style={{ flex: 1 }}><Input label="Préavis (jours)" type="number" value={form.preavisJours} onChange={e => setForm(f => ({ ...f, preavisJours: e.target.value }))} /></div>
            <div style={{ flex: 1 }}><Input label="Fait à" value={form.faitA} onChange={e => setForm(f => ({ ...f, faitA: e.target.value }))} /></div>
          </div>
          <Input label="Représentant du bailleur" value={form.bailleurRepresentant} onChange={e => setForm(f => ({ ...f, bailleurRepresentant: e.target.value }))} placeholder="Nom du représentant IAM" />

          {/* ── Locataire 1 ── */}
          <p style={S.section}>Locataire 1</p>
          <Input label="Nom et prénom *" value={loc1.nom} onChange={e => setLoc1(l => ({ ...l, nom: e.target.value }))} />
          <div style={{ display: "flex", gap: 10 }}>
            <div style={{ flex: 1 }}><Input label="Date de naissance" type="date" value={loc1.naissance} onChange={e => setLoc1(l => ({ ...l, naissance: e.target.value }))} /></div>
            <div style={{ flex: 1 }}><Input label="Téléphone" value={loc1.telephone} onChange={e => setLoc1(l => ({ ...l, telephone: e.target.value }))} placeholder="+223 ..." /></div>
          </div>
          <Input label="Établissement" value={loc1.etablissement} onChange={e => setLoc1(l => ({ ...l, etablissement: e.target.value }))} placeholder="IPR/IFRA de Katibougou" />

          {/* ── Locataire 2 optionnel ── */}
          <button style={S.toggle} onClick={() => setDeuxLocataires(v => !v)}>
            {deuxLocataires ? "− Retirer le locataire 2" : "+ Ajouter un locataire 2 (colocation)"}
          </button>
          {deuxLocataires && (
            <div style={{ background: "#f7fafd", borderRadius: 12, padding: "12px 12px 4px", marginBottom: 12 }}>
              <Input label="Nom et prénom *" value={loc2.nom} onChange={e => setLoc2(l => ({ ...l, nom: e.target.value }))} />
              <div style={{ display: "flex", gap: 10 }}>
                <div style={{ flex: 1 }}><Input label="Date de naissance" type="date" value={loc2.naissance} onChange={e => setLoc2(l => ({ ...l, naissance: e.target.value }))} /></div>
                <div style={{ flex: 1 }}><Input label="Téléphone" value={loc2.telephone} onChange={e => setLoc2(l => ({ ...l, telephone: e.target.value }))} /></div>
              </div>
              <Input label="Établissement" value={loc2.etablissement} onChange={e => setLoc2(l => ({ ...l, etablissement: e.target.value }))} />
            </div>
          )}

          {/* ── Signatures ── */}
          <p style={S.section}>Signatures (sur cet écran)</p>
          <SignaturePad label="✍️ Signature du bailleur (IAM)" onChange={setSigBailleur} />
          <SignaturePad label={`✍️ Signature de ${loc1.nom.trim() || "locataire 1"}`} onChange={setSigLoc1} />
          {deuxLocataires && <SignaturePad label={`✍️ Signature de ${loc2.nom.trim() || "locataire 2"}`} onChange={setSigLoc2} />}

          {error && <p style={{ fontSize: 12, color: "#a32d2d", fontWeight: 600 }}>{error}</p>}

          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 6 }}>
            <Btn variant="primary" onClick={handleGenerate} disabled={!canGenerate || generating}>
              {generating ? "Génération..." : "📜 Générer et signer le contrat"}
            </Btn>
            {!canGenerate && (
              <p style={{ fontSize: 11, color: "#8a4c00", margin: 0, textAlign: "center" }}>
                Nom du locataire, date d'entrée et les signatures sont obligatoires
              </p>
            )}
            <Btn variant="ghost" onClick={onClose}>Annuler</Btn>
          </div>
        </div>
      )}

      {done && (
        <div style={{ textAlign: "center", padding: "20px 0" }}>
          <div style={{ width: 64, height: 64, borderRadius: "50%", background: "#e8f5e0", color: "#2d6a0a", fontSize: 30, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>✓</div>
          <p style={{ fontSize: 17, fontWeight: 800, color: "#1a3a5c", margin: "0 0 6px" }}>Contrat généré !</p>
          <p style={{ fontSize: 13, color: "#5a7a9a", lineHeight: 1.6, margin: "0 0 18px" }}>
            Le PDF signé a été téléchargé sur cet appareil et archivé dans le dossier de la chambre {room?.number}.
          </p>
          <Btn variant="primary" onClick={onClose}>Fermer</Btn>
        </div>
      )}
    </Modal>
  );
}

const S = {
  section: { fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#7a90a8", margin: "14px 0 8px" },
  toggle: { background: "#f0f4f8", border: "1.5px dashed #b0c4d8", borderRadius: 10, padding: "9px", width: "100%", fontSize: 12.5, fontWeight: 600, color: "#1a3a5c", cursor: "pointer", fontFamily: "inherit", marginBottom: 12 },
};
